import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {DataTable} from 'notebook/data-tables/DataTableConstants';
import {addRowsToStageRunSampleTable} from 'notebook/data-tables/DataTableUtil';
import getNoteLinkedObjectsOfTypes from 'notebook/notes/util/getNoteLinkedObjectsOfTypes';
import {RegistrationTable} from 'notebook/registration-tables/RegistrationTableConstants';
import {Note} from 'notebook/util/NoteUtilConstants';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import {getInputSampleKeys} from 'workflows/util/StageRunUtil';
import {
  INPUT_SAMPLES_SOURCE_UUID,
  RegistrationRecordList,
  StageRun,
  StageRunSample,
} from 'workflows/WorkflowConstants';

export function addRowsForInputSamplesToDataTable({
  dataTable,
  stageRuns,
  stageRunSampleById,
}: {
  dataTable: DataTable;
  stageRuns: Array<StageRun>;
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
}): DataTable {
  const sampleIdsAlreadyInTable = new Set(dataTable.rows.map((row) => row.stageRunSampleId));

  const validRowKeys = getInputSampleKeys(stageRuns).filter(
    ({stageRunSampleId}: {stageRunSampleId: string}) => {
      const sample = stageRunSampleById[stageRunSampleId];
      if (!sample) {
        return false;
      }

      if (sampleIdsAlreadyInTable.has(stageRunSampleId)) {
        return false;
      }

      if (dataTable.config.linkType === FIELD_TYPES.BATCH_LINK.type) {
        return sample.batch;
      } else {
        assert(dataTable.config.linkType === FIELD_TYPES.BIOENTITY_LINK.type, 'Unexpected linkType');
        return sample.fileId;
      }
    }
  );

  return addRowsToStageRunSampleTable(dataTable, validRowKeys);
}

export function addRowsForRegisteredSamplesToDataTable(
  dataTable: DataTable,
  registrationRecordList: RegistrationRecordList
): DataTable {
  const sampleIdsAlreadyInTable = new Set(dataTable.rows.map((row) => row.stageRunSampleId));

  const newRowKeys = registrationRecordList.registrationRecords
    .map(({stageRunId, stageRunSampleId}) => ({stageRunId, stageRunSampleId}))
    .filter(({stageRunSampleId}) => !sampleIdsAlreadyInTable.has(stageRunSampleId));

  return addRowsToStageRunSampleTable(dataTable, newRowKeys);
}

export function getDataTablesWatchingRegistrationTable(
  note: Note,
  registrationTable: RegistrationTable
): Array<DataTable> {
  const dataTables = getNoteLinkedObjectsOfTypes(note, [ModelTypeConstants.DATA_TABLE]) as Array<DataTable>;
  return dataTables.filter((dataTable) =>
    dataTable.config.watchedRegistrationProvenanceUuids.includes(registrationTable.provenanceUuid)
  );
}

export function autopopulateWithNewSources({
  dataTable,
  stageRuns,
  registrationTables,
  registrationRecordListsById,
  newSourceTableUuids,
  stageRunSampleById,
}: {
  dataTable: DataTable;
  stageRuns: Array<StageRun>;
  registrationTables: Array<RegistrationTable>;
  registrationRecordListsById: {[id: string]: RegistrationRecordList};
  newSourceTableUuids: Array<string>;
  stageRunSampleById: {[stageRunSampleId: string]: StageRunSample};
}): DataTable {
  let newDataTable = dataTable;
  for (const sourceTableUuid of newSourceTableUuids) {
    if (sourceTableUuid === INPUT_SAMPLES_SOURCE_UUID) {
      newDataTable = addRowsForInputSamplesToDataTable({
        dataTable: newDataTable,
        stageRuns,
        stageRunSampleById,
      });
    } else {
      const registrationTable = registrationTables.find((table) => table.provenanceUuid === sourceTableUuid);
      if (registrationTable) {
        const registrationRecordListId = registrationTable.registrationRecordListId;
        if (registrationRecordListId != null && registrationRecordListsById[registrationRecordListId]) {
          newDataTable = addRowsForRegisteredSamplesToDataTable(
            newDataTable,
            registrationRecordListsById[registrationRecordListId]
          );
        }
      }
    }
  }
  return newDataTable;
}
